import './styles/Grouping.scss';

import { useMutation } from '@apollo/client';
import { Grid } from '@mui/material';

import { Formik } from 'formik';
import { omit, omitBy, pick } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import CREATE_MUTATION from 'gql/mutations/ModulesService/addGrouping.gql';
import UPDATE_MUTATION from 'gql/mutations/ModulesService/updateGrouping.gql';

import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { validateForm } from 'utils/FormErrorHelper';

import { Grouping } from '../../ModuleSelections/models';
import { GroupingInformation } from './components/GroupingInformtation';
import { groupingValidationSchema } from './validation';

interface Props {
  closeHandler: Function;
  setDisabledCancel: Function;
  grouping?: Grouping | null;
}

export function GroupingModal(props: Props) {
  const [submitting, setSubmitting] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [createGrouping] = useMutation(CREATE_MUTATION, { context: { clientName: 'federation' } });
  const [updateGrouping] = useMutation(UPDATE_MUTATION, { context: { clientName: 'federation' } });

  const initialValues = () => {
    return props.grouping
      ? {
          ...pick(props.grouping, ['id', 'name', 'description'])
        }
      : { name: '', description: '' };
  };

  const handleSaveClick = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);

    const omitFields = ['id'];
    let saveBody: any = {
      ...omit(values, omitFields)
    };
    if (values.id) {
      try {
        await updateGrouping({
          variables: {
            id: parseInt(values.id, 10),
            data: omitBy(saveBody, v => v === null)
          },
          refetchQueries: ['GetGroupings']
        });
        dispatch(
          AlertActions.success(t(translations.pages.groupings.alerts.successEditingGrouping()))
        );
      } catch (err) {
        dispatch(
          AlertActions.error(t(translations.pages.groupings.alerts.errorEditingGroupings()))
        );
      }
    } else {
      try {
        await createGrouping({
          variables: { data: omitBy(saveBody, v => v === null) },
          refetchQueries: ['GetGroupings']
        });
        dispatch(
          AlertActions.success(t(translations.pages.groupings.alerts.successCreatingGrouping()))
        );
      } catch (err) {
        dispatch(
          AlertActions.error(t(translations.pages.groupings.alerts.errorCreatingGrouping()))
        );
      }
    }
    props.closeHandler();
  };

  const steps = ['Information '];

  const SettingsModalStep = ({ step }) => {
    switch (step) {
      case 0:
        return <GroupingInformation />;
      default:
        return null;
    }
  };

  return (
    <Grid item className="groupingModal">
      <Formik
        isInitialValid={!!props.grouping}
        initialValues={initialValues()}
        onSubmit={values => handleSaveClick(values)}
        validationSchema={groupingValidationSchema(t)}
        validate={values => {
          return validateForm(values, groupingValidationSchema(t), dispatch);
        }}
      >
        <MultistepForm
          submitting={submitting}
          steps={steps}
          onCancel={props.closeHandler}
          hideStepper
        >
          {activeStep => (
            <Grid container className="groupingModal__body">
              <SettingsModalStep step={activeStep} />
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
}
